// src/stores/usePesaEngine.js
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { PesaEngine } from '../engine/PesaEngineCore';
import pesaSocket from '../socket/pesaSocket';

export const COMMERCIAL_LIVE_INITIAL_STATE = {
  // Mode et token de session
  commercialLive: {
    enabled:       false,       // true quand une session live est active
    token:         null,        // token de session partagé vendeur/client
    role:          null,        // 'seller' | 'client' | null
    connected:     false,
 
    // Events reçus du client (côté vendeur) ou envoyés (côté client)
    // Chaque event : { id, type, choiceId|text, phaseKey, sentAt, receivedAt, status }
    // status : 'pending' | 'compiled' | 'rejected'
    clientEvents:         [],
    pendingClientEvents:  [],   // subset de clientEvents status === 'pending'
    compiledClientEvents: [],   // subset compilés dans le débrief
 
    // Débrief final enrichi avec les inputs client
    clientInputs: [],           // { phaseKey, prompt, text, compiledAt }
    clientChoices: [],          // { phaseKey, prompt, choiceId, label, effect, compiledAt }
  },
};

export const usePesa = create(
  devtools((set, get) => {
    if (typeof window !== 'undefined') {
      window.__PESA_STORE__ = { setState: set };
      window.__PESA_GET_STATE__ = get;                           
    }

    return {
      // === ÉTAT COMPLET ===
      engine: null,
      scenario: null,
      phase: 'loading', // loading | playing | finished
      phaseTimer: null,      

      // Données live
      parameters: {},
      currentStep: null,
      currentStepIndex: 0,
      completedActions: new Set(), 
      unlockedActions: new Set(),
      solutions: new Set(),
      highlightedFamilies: new Set(),
      lastAction: null,
      history: [],
      narrativeOverride: null,
      currentSolution: null,
      remainingTime: null,
      isCalibrating: false,

      // UI
      openPanels: {
        dynamics: true,
        indicators: true,
        stepguide: true,
        history: false,
        causal: false,
        prediction: false,
        theory: false,
        insight: false,
        counterfactuals: false,
        champVivant: false,
        champVivantFullscreen: false,
        theoryFullscreen: false,
      },

      graphLayout: 'circle',
      theoryMode: false,
      isOptimizing: false,
      stepGuideMode: 'overlay',
      showPredictionMethod: false,
      showDebrief: false,
      showSolution: false,

      // Phases gérées par le moteur
      currentPhaseIndex: 0,
      currentPhase: null,
      isPhaseRunning: true,

      ...COMMERCIAL_LIVE_INITIAL_STATE,
      
      getPesaState: (caseId) => {
        const state = get();
        const engineState = state.engine?.state;

        const pesaState = {
          emancipationScore: state.getEmancipationScore(),
          currentSolution: engineState?.currentSolution,
          parameters: engineState?.parameters instanceof Map 
            ? Object.fromEntries(engineState.parameters) 
            : engineState?.parameters,
          // On s'assure que chaque agent envoyé possède bien son bloc theory
          agents: (engineState?.agents || []).map(agent => ({
            ...agent,
            theory: agent.theory || null // Force l'inclusion de la théorie
          })),
          completedActions: Array.from(engineState?.completedActions || []),
          families: engineState?.families,
        };

        pesaSocket.emit('update-state', { caseId, state: pesaState });
  
        // Sauvegarde locale pour la vue conférence
        localStorage.setItem(`pesa_sync_${caseId}`, JSON.stringify({
          case: state.scenario,
          state: pesaState
        }));
      },

      joinPesaCase: (caseId) => {
        pesaSocket.emit('join-case', caseId);
      },

      notifyUpdate: () => {
        
      },  
      
      getProcessMap: () => {
        const { currentStepIndex, completedActions, currentSolution } = get();

        const nodes = [
          { id: "start", label: "Domination symbolique", type: "start", active: true },
          { id: "diagnostic", label: "Diagnostic", type: "step", active: currentStepIndex >= 1 },
          { id: "reactivation", label: "Réactivation", type: "step", active: completedActions.has("reactiver_art") || completedActions.has("instituer_mediation") },
          { id: "subversion", label: "Subversion", type: "step", active: completedActions.has("art_subversif") || completedActions.has("mediation_transformative") },
          { id: "institutionnalisation", label: "Institutionnalisation", type: "step", active: completedActions.has("projet_etablissement") || completedActions.has("radicalisation_totale") },
          { id: "debrief", label: "Débrief théorique", type: "step", active: currentStepIndex >= 6 },
          { id: "end", label: currentSolution?.name || "Fin", type: "end", active: true }
        ];

        const edges = [
          { from: "start", to: "diagnostic", active: currentStepIndex >= 1 },
          { from: "diagnostic", to: "reactivation", active: completedActions.size > 0 },
          { from: "reactivation", to: "subversion", active: completedActions.has("art_subversif") || completedActions.has("mediation_transformative") },
          { from: "subversion", to: "institutionnalisation", active: completedActions.has("projet_etablissement") || completedActions.has("radicalisation_totale") },
          { from: "institutionnalisation", to: "debrief", active: currentStepIndex >= 6 },
          { from: "debrief", to: "end", active: true }
        ];

        return { nodes, edges };
      },

      getProcessMapPro: () => {
        const { parameters = {}, completedActions = new Set(), currentStepIndex = 0 } = get();

        // Normalisation
        const norm = (v) => (v ?? 50) / 100;
        const bienEtre = norm(parameters.bienEtre);
        const equite = norm(parameters.equite);
        const creativite = norm(parameters.creativite);
        const mediation = norm(parameters.mediation);
        const apprentissage = norm(parameters.apprentissage);

        // Leviers activés
        const artActive = completedActions.has("reactiver_art") || completedActions.has("art_subversif");
        const mediationActive = completedActions.has("instituer_mediation") || completedActions.has("mediation_transformative");
        const notesSupprimees = completedActions.has("radicalisation_totale");
        const gouvernanceDemocratisee = completedActions.has("projet_etablissement");

        // Étape actuelle (pour feedback narratif)
        const stage = currentStepIndex <= 1 ? "diagnostic" :
                      currentStepIndex <= 2 ? "réactivation" :
                      currentStepIndex <= 3 ? "subversion" :
                      currentStepIndex <= 5 ? "institutionnalisation" : "debrief";

        const nodes = [
          { id: "orientation", label: "Orientation & Parcoursup", strength: 0.9 - equite * 0.4, status: equite > 0.7 ? "attenuée" : "forte" },
          { id: "evaluation", label: "Évaluation & Notes", strength: notesSupprimees ? 0.1 : 0.95, status: notesSupprimees ? "neutralisée" : "dominante" },
          { id: "discipline", label: "Discipline & Sanctions", strength: mediationActive ? 0.2 : 0.85, status: mediationActive ? "transformée" : "dominante" },
          { id: "formation_profs", label: "Formation continue", strength: 0.4 + (artActive || mediationActive ? 0.35 : 0), status: artActive || mediationActive ? "innovante" : "rigide" },
          { id: "gouvernance", label: "Conseil d’établissement", strength: gouvernanceDemocratisee ? 0.8 : 0.3, status: gouvernanceDemocratisee ? "démocratisée" : "hiérarchique" },
          { id: "relations_parents", label: "Relations familles", strength: 0.5 + (mediationActive ? 0.3 : 0), status: mediationActive ? "renforcées" : "distantes" },
          { id: "climat", label: "Climat scolaire", strength: bienEtre, status: bienEtre > 0.7 ? "apaisé" : bienEtre < 0.4 ? "dégradé" : "fragile" },
          { id: "performance", label: "Résultats académiques", strength: apprentissage, status: apprentissage > 0.8 ? "élevés" : "moyens" },
          { id: "equite_sociale", label: "Équité sociale", strength: equite, status: equite > 0.7 ? "renforcée" : "fragile" }
        ];

        const edges = [
          { from: "evaluation", to: "climat", weight: notesSupprimees ? -0.2 : -0.8, label: "anxiété" },
          { from: "evaluation", to: "performance", weight: 0.9, label: "résultats" },
          { from: "discipline", to: "climat", weight: mediationActive ? -0.2 : -0.7, label: "exclusions" },
          { from: "gouvernance", to: "climat", weight: gouvernanceDemocratisee ? 0.7 : 0.3, label: "participation" },
          { from: "relations_parents", to: "climat", weight: 0.5 + (mediationActive ? 0.2 : 0), label: "confiance" },
          { from: "climat", to: "performance", weight: -0.6 + bienEtre * 0.5, label: "décrochage" },
          { from: "orientation", to: "equite_sociale", weight: -0.75 + equite * 0.4, label: "reproduction" },
          { from: "formation_profs", to: "evaluation", weight: artActive ? -0.4 : 0.1, label: "innovation" },
          { from: "formation_profs", to: "discipline", weight: mediationActive ? -0.3 : 0.1, label: "restauratif" }
        ];

        return { nodes, edges, stage };
      },

      getEmancipationScore() {
        const { parameters = {}, families = [], completedActions = new Set() } = get();

        const bienEtreNorm = (parameters.bienEtre || 50) / 100;
        const equiteNorm = (parameters.equite || 50) / 100;
        const creativiteNorm = (parameters.creativite || 50) / 100;
        const mediationNorm = (parameters.mediation || 50) / 100;

        const famillesToxiques = families.filter(f => f.isToxic && f.isActive).length;
        const famillesEmancipatricesActives = families.filter(
          f => f.isActive && (f.name === "Art" || f.name === "Médiation")
        ).length;

        let score = bienEtreNorm * 0.35 + equiteNorm * 0.30 + creativiteNorm * 0.20 + mediationNorm * 0.15;

        if (famillesEmancipatricesActives === 2) score += 0.25;
        else if (famillesEmancipatricesActives === 1) score += 0.10;

        if (famillesToxiques > 0) score *= 0.6;

        return Math.min(100, Math.round(score * 100));
      },
      
      initStepGuideMode: () => {
        const saved = localStorage.getItem('pesa-stepguide-mode');
        if (saved === 'overlay' || saved === 'sidebar') {
          set({ stepGuideMode: saved });
        }
      },
      
      toggleStepGuideMode: () => {
        set(state => {
          const newMode = state.stepGuideMode === 'overlay' ? 'sidebar' : 'overlay';
          localStorage.setItem('pesa-stepguide-mode', newMode);
          return { stepGuideMode: newMode };
        });
      },

      togglePredictionMethod: (value) => set(state => ({
        showPredictionMethod: value !== undefined ? value : !state.showPredictionMethod
      })),

      loadBuilderScenario: (builderScenario) => {
        // 1. Nettoyage de l'état précédent
        set({
          engine: null,
          scenario: null,
          phase: 'loading',
          parameters: {},
          initialParameters: {},
          families: [],
          agents: [],
          currentStep: null,
          currentStepIndex: 0,
          completedActions: new Set(),
          unlockedActions: new Set(),
          solutions: new Set(),
          highlightedFamilies: new Set(),
          lastAction: null,
          history: [],
          narrativeOverride: null,
          currentSolution: null,
          remainingTime: null,
          phaseTimer: null,
          isCalibrating: false,
          openPanels: {
            dynamics: true,
            indicators: true,
            stepguide: true,
            history: false,
            causal: false,
            prediction: false,
            theory: false,
            insight: false,
            counterfactuals: false,
            champVivant: false,
            champVivantFullscreen: false,
            theoryFullscreen: false,
          },
        });

        // 2. Normalisation du scénario : on s'assure que toutes les propriétés
        //    nécessaires au moteur existent, avec des valeurs par défaut.
        const scenario = {
          slug: builderScenario.slug || 'builder-scenario',
          title: builderScenario.title || 'Scénario sans titre',
          pedagogicalGoal: builderScenario.pedagogicalGoal || '',
          displayMode: builderScenario.displayMode || 'vente',
          theme: builderScenario.theme || 'light',
          initialState: {
            parameters: builderScenario.initialState?.parameters || {},
            families: builderScenario.initialState?.families || [],
            agents: builderScenario.initialState?.agents || [],
            interactions: builderScenario.initialState?.interactions || [],
            theory: builderScenario.initialState?.theory || null,
            source: builderScenario.initialState?.source || null,
          },
          steps: builderScenario.steps || [],
          simulationEngine: {
            dynamics: builderScenario.simulationEngine?.dynamics || [],
            computedMetrics: builderScenario.simulationEngine?.computedMetrics || {},
            network: builderScenario.simulationEngine?.network || [],
            quotes: builderScenario.simulationEngine?.quotes || [],
            predictionModel: builderScenario.simulationEngine?.predictionModel || { enabled: false },
            calibration: builderScenario.simulationEngine?.calibration || { enabled: false },
            processMining: builderScenario.simulationEngine?.processMining || { enabled: false },
            scenarioEngine: builderScenario.simulationEngine?.scenarioEngine || { enabled: false },
            logs: builderScenario.simulationEngine?.logs || { store: false },
          },
          solutions: builderScenario.solutions || [],
          counterfactuals: builderScenario.counterfactuals || [],
          mediaAssets: builderScenario.mediaAssets || [], // le moteur n'en a pas besoin, mais on le garde dans le scénario
          // Autres champs éventuels
          sources: builderScenario.sources || [],
          processMap: builderScenario.processMap || null,
          processMapPro: builderScenario.processMapPro || null,
          optimizationGoals: builderScenario.optimizationGoals || null,
          optimalPath: builderScenario.optimalPath || null,
          metadata: builderScenario.metadata || {},
        };

        // 3. Instanciation du moteur avec ce scénario normalisé
        let freshEngine;
        try {
          freshEngine = new PesaEngine(scenario, { startTimer: false });
        } catch (err) {
          console.error('Erreur lors de la création du moteur depuis le builder :', err);
          set({ phase: 'error' });
          return;
        }

        const state = freshEngine.state;

        // 4. Peuplement du store avec les données du moteur
        set({
          engine: freshEngine,
          scenario: scenario,
          phase: 'playing',
          currentCase: scenario,

          parameters: { ...state.parameters },
          initialParameters: { ...state.initialParameters },
          families: state.families || [],
          agents: state.agents || [],
          interactions: state.interactions || [],
          solutions: state.solutions || [],

          currentStep: freshEngine.getCurrentStep(),
          currentStepIndex: state.currentStepIndex || 0,
          completedActions: new Set(state.completedActions || []),
          unlockedActions: freshEngine.unlockedActions ? freshEngine.unlockedActions() : new Set(),
          highlightedFamilies: state.highlightedFamilies || new Set(),
          lastAction: null,
          history: [...freshEngine.history],
          narrativeOverride: null,
          currentSolution: null,
          counterfactuals: scenario.counterfactuals || [],
          currentPhaseIndex: state.currentPhaseIndex || 0,
          currentPhase: state.currentPhase,
          isPhaseRunning: false,
          remainingTime: state.remainingTime,
          phaseTimer: null,
          showDebrief: false,
          showSolution: false,
          graphLayout: 'circle',
          theoryMode: false,
          isOptimizing: false,
          processMap: get().getProcessMap ? get().getProcessMap() : null,
          processMapPro: get().getProcessMapPro ? get().getProcessMapPro() : null,
          openPanels: {
            dynamics: true,
            indicators: true,
            stepguide: true,
            history: false,
            causal: false,
            prediction: false,
            theory: false,
            insight: false,
            counterfactuals: false,
            champVivant: false,
            champVivantFullscreen: false,
            theoryFullscreen: false,
          },
        });

        // Lancer la phase initiale si nécessaire
        if (state.currentPhase?.duration > 0) {
          freshEngine._schedulePhaseAdvance(state.currentPhase.duration);
        }
        freshEngine.state.isPhaseRunning = true;
        freshEngine._notifyStore();

        get().joinPesaCase(scenario.slug || 'builder-case');
      },

      // === ACTIONS ===
      loadSimulation: (caseData) => {
        set({
          engine: null,
          scenario: null,
          phase: 'loading',
          parameters: {},
          initialParameters: {},
          families: [],
          agents: [],
          currentStep: null,
          currentStepIndex: 0,
          completedActions: new Set(),
          unlockedActions: new Set(),
          solutions: new Set(),
          highlightedFamilies: new Set(),
          lastAction: null,
          history: [],
          narrativeOverride: null,
          currentSolution: null,
          remainingTime: null,
          phaseTimer: null,
          isCalibrating: false,
          // Reset tous les autres états que tu as
          openPanels: {
            dynamics: true,
            indicators: true,
            stepguide: true,
            history: false,
            causal: false,
            prediction: false,
            theory: false,
            insight: false,
            counterfactuals: false,
            champVivant: false,
            champVivantFullscreen: false,
            theoryFullscreen: false,
          },
        });
  

        // Création d'un moteur Frais à chaque load
        const freshEngine = new PesaEngine(caseData, { startTimer: false });
        const state = freshEngine.state;       
        console.log("Moteur chargé avec agents:", state.agents?.length);

        get().initStepGuideMode();
      
        set({
          engine:freshEngine,
          scenario: caseData,
          phase: 'playing',
          currentCase: caseData,

          parameters: { ...freshEngine.state.parameters },
          initialParameters: { ...freshEngine.state.initialParameters }, 
          families: state.families || [], 
          agents: state.agents || [], // ICI : Transfert direct
          interactions: state.interactions || [],
          solutions: state.solutions || [],      // ← ajouter
          currentStep: freshEngine.getCurrentStep(),
          currentStepIndex: freshEngine.state.currentStepIndex || 0,
          completedActions: new Set(freshEngine.state.completedActions || []),
          unlockedActions: freshEngine.unlockedActions ? freshEngine.unlockedActions() : new Set(),
          highlightedFamilies: freshEngine.state.highlightedFamilies || new Set(),
          lastAction: null,
          history: [...freshEngine.history],
          narrativeOverride: null,
          currentSolution: null,
          counterfactuals: caseData.counterfactuals || [],
          currentPhaseIndex: freshEngine.state.currentPhaseIndex || 0,
          currentPhase: freshEngine.state.currentPhase,
          isPhaseRunning: false,
          remainingTime: freshEngine.state.remainingTime,
          phaseTimer: null,
          showDebrief: false,
          showSolution: false,
          graphLayout: 'circle',
          theoryMode: false,
          isOptimizing: false,
          processMap: get().getProcessMap(),
          processMapPro: get().getProcessMapPro(),
          openPanels: {
            dynamics: true,
            indicators: true,
            stepguide: true,
            history: false,
            causal: false,
            prediction: false,
            theory: false,
            insight: false,
            counterfactuals: false,
            champVivant: false,
            champVivantFullscreen: false,
            theoryFullscreen: false,
          },
        });
        get().joinPesaCase(caseData.slug || 'default');
      },

      applyAction: (actionId) => {
        const { engine, phase, scenario } = get();
        if (!engine || phase !== 'playing') return;

        const result = engine.applyAction(actionId);
        if (!result.success) return;

        const isFinished = engine.isSimulationFinished();  

        
        const newStep = engine.getCurrentStep(); // après l'action, peut avoir changé de step
        const narrativeText = newStep?.narrativeOverride || null;

        let reachedSolution = null;
        if (scenario?.solutions) {
          reachedSolution = scenario.solutions.find(sol =>
            sol.requiredActions.length > 0 &&
            sol.requiredActions.every(id =>
              [...engine.state.completedActions].some(c => c.trim() === id.trim())
            )
          ) || null;
        }              

        set({
          parameters: { ...engine.state.parameters },
          initialParameters: { ...engine.state.initialParameters },
          families: engine.state.families,
          agents: engine.state.agents,
          interactions: engine.state.interactions,
          solutions: engine.state.solutions,
          currentStep: engine.getCurrentStep(),
          currentStepIndex: engine.state.currentStepIndex,
          completedActions: new Set(engine.state.completedActions || []),
          unlockedActions: engine.unlockedActions ? engine.unlockedActions() : new Set(),
          highlightedFamilies: engine.state.highlightedFamilies || new Set(),
          lastAction: actionId,
          history: [...engine.history],
          currentSolution: reachedSolution || get().currentSolution,
          currentPhaseIndex: engine.state.currentPhaseIndex || 0,
          currentPhase: engine.state.currentPhase,
          isPhaseRunning: engine.state.isPhaseRunning,
          counterfactuals: engine.counterfactuals,
          //showDebrief: isFinished,
          showDebriefOnboarding: true,
          showSolution: false,
          narrativeOverride: narrativeText, 
          processMap: get().getProcessMap(),
          processMapPro: get().getProcessMapPro(),
          currentCase: scenario,
          openPanels: {
            dynamics: true,
            indicators: true,
            stepguide: true,
            history: false,
            causal: false,
            prediction: false,
            theory: false,
            insight: false,
            counterfactuals: false,
            champVivant: false,
            champVivantFullscreen: false,
            theoryFullscreen: false,
          },
        });

        if (reachedSolution) {
          set({ showDebrief: false });
        }
      },

      updateEngineState: (partialState) => set(state => ({
        engine: state.engine ? {
          ...state.engine,
          state: {
            ...state.engine.state,
            ...partialState
          }
        } : null
      })),

      toggleFloatingPanel: (key, force) => {
        set((state) => ({
          openPanels: {
            ...state.openPanels,
            [key]: force !== undefined ? force : !state.openPanels[key],
          },
        }));
      },   
      
      commercialLiveInit: ({ token, role }) => {
        set(state => ({
          commercialLive: {
            ...state.commercialLive,
            enabled:   true,
            token,
            role,
            connected: false,
            clientEvents:         [],
            pendingClientEvents:  [],
            compiledClientEvents: [],
            clientInputs:         [],
            clientChoices:        [],
          },
        }));
      },

      commercialLiveSetConnected: (connected) => {
        set(state => ({
          commercialLive: { ...state.commercialLive, connected },
        }));
      },

      commercialLiveReceiveEvent: (event) => {
        set(state => {
          const cl = state.commercialLive;
    
          // Déduplique par id
          if (cl.clientEvents.some(e => e.id === event.id)) return state;
    
          const enriched = { ...event, status: 'pending' };
          const updated  = [...cl.clientEvents, enriched];
    
          return {
            commercialLive: {
              ...cl,
              clientEvents:        updated,
              pendingClientEvents: updated.filter(e => e.status === 'pending'),
            },
          };
        });
      },

      commercialLiveCompileEvents: () => {
        const { commercialLive, engine, scenario } = get();
        if (!commercialLive.enabled) return [];
    
        const pending = commercialLive.pendingClientEvents;
        if (!pending.length) return [];
    
        const now         = Date.now();
        const compiledIds = [];
        const newInputs   = [...commercialLive.clientInputs];
        const newChoices  = [...commercialLive.clientChoices];
    
        // Récupérer la clé de phase courante pour contextualisation
        const phaseKey = engine
          ? `${engine.state.currentStepIndex}-${engine.state.currentPhaseIndex}`
          : 'unknown';
    
        for (const event of pending) {
          if (event.type === 'client_choice') {
            // Appliquer l'effet de ce choix sur les paramètres via le moteur
            if (event.effect && engine) {
              set(state => {
                const newParams = { ...state.parameters }; // ou engine.state.parameters
                for (const [key, delta] of Object.entries(event.effect)) {
                  if (typeof newParams[key] === 'number') {
                    newParams[key] = Math.max(0, Math.min(100, (newParams[key] || 0) + delta));
                  }
                }
                // Appliquer aussi à engine.state.parameters pour cohérence
                if (state.engine) {
                  state.engine.state.parameters = newParams;
                }
                return { parameters: newParams };
              });
            }
    
            newChoices.push({
              id:          event.id,
              phaseKey,
              prompt:      event.prompt    || '',
              choiceId:    event.choiceId  || '',
              label:       event.label     || '',
              effect:      event.effect    || {},
              compiledAt:  now,
            });
            compiledIds.push(event.id);
    
          } else if (event.type === 'client_input') {
            newInputs.push({
              id:         event.id,
              phaseKey,
              prompt:     event.prompt   || '',
              fieldLabel: event.fieldLabel || '',
              text:       event.text      || '',
              compiledAt: now,
            });
            compiledIds.push(event.id);
          }
        }
    
        // Mettre à jour le state
        set(state => {
          const cl      = state.commercialLive;
          const updated = cl.clientEvents.map(e =>
            compiledIds.includes(e.id) ? { ...e, status: 'compiled' } : e
          );
          return {
            // Resynchroniser les paramètres après application des effets client
            parameters: engine ? { ...engine.state.parameters } : state.parameters,
            commercialLive: {
              ...cl,
              clientEvents:         updated,
              pendingClientEvents:  [],
              compiledClientEvents: [...cl.compiledClientEvents, ...updated.filter(e => compiledIds.includes(e.id))],
              clientInputs:         newInputs,
              clientChoices:        newChoices,
            },
          };
        });
    
        // Notifier le serveur que ces events sont compilés (fire & forget)
        const { commercialLive: cl } = get();
        if (cl.token && compiledIds.length) {
          fetch(`${process.env.REACT_APP_API_BASE_URL || ''}/commercial/session/${cl.token}/compile`, {
            method:      'PATCH',
            credentials: 'include',
            headers:     { 'Content-Type': 'application/json' },
            body:        JSON.stringify({ eventIds: compiledIds }),
          }).catch(() => {});
        }
    
        return compiledIds;
      },

      commercialLiveAdvancePhase: () => {
        const { engine, commercialLive } = get();
        if (!engine) return;
    
        // 1. Compiler les events client en attente
        if (commercialLive.enabled && commercialLive.pendingClientEvents.length > 0) {
          get().commercialLiveCompileEvents();
        }
    
        // 2. Avancer la phase dans le moteur (logique inchangée)
        engine.advancePhase();
    
        // 3. Resynchroniser le store avec le nouvel état moteur
        const state = engine.state;
        set({
          parameters:        { ...state.parameters },
          currentStep:       engine.getCurrentStep(),
          currentStepIndex:  state.currentStepIndex,
          currentPhase:      state.currentPhase,
          currentPhaseIndex: state.currentPhaseIndex,
          remainingTime:     state.remainingTime,
          isPhaseRunning:    state.isPhaseRunning,
        });
    
        // 4. Si mode live vendeur, broadcaster l'avance de phase via le hook
        // (le hook est externe — on stocke juste l'info pour que CommercialPage la lise)
        if (commercialLive.enabled && commercialLive.role === 'seller') {
          set(state => ({
            commercialLive: {
              ...state.commercialLive,
              lastPhaseAdvance: {
                currentStepIndex:  engine.state.currentStepIndex,
                currentPhaseIndex: engine.state.currentPhaseIndex,
                ts:                Date.now(),
              },
            },
          }));
        }
      },

      commercialLiveReset: () => {
        set(() => ({
          commercialLive: {
            ...COMMERCIAL_LIVE_INITIAL_STATE.commercialLive,
          },
        }));
      },

      commercialLiveGetDebriefData: () => {
        const { commercialLive } = get();
        if (!commercialLive.enabled) return null;
    
        return {
          clientChoices: commercialLive.clientChoices,
          clientInputs:  commercialLive.clientInputs,
          totalEvents:   commercialLive.clientEvents.length,
          compiled:      commercialLive.compiledClientEvents.length,
          // Synthèse des effets client appliqués
          choiceEffectSummary: commercialLive.clientChoices.reduce((acc, c) => {
            Object.entries(c.effect || {}).forEach(([k, v]) => {
              acc[k] = (acc[k] || 0) + v;
            });
            return acc;
          }, {}),
        };
      },

      setGraphLayout: (layout) => set({ graphLayout: layout }),      
      toggleTheoryMode: () => set((state) => ({ theoryMode: !state.theoryMode })),
      setNarrativeOverride: (text) => set({ narrativeOverride: text }),
      toggleShowDebrief: () => set((state) => ({ showDebrief: !state.showDebrief })),
      closeDebrief: () => set({ showDebrief: false }),
      closeSolution: () => set({ showSolution: false }),
      
      calibrateRealProbability: async () => {
        const { engine, showDebrief } = get();
        if (!showDebrief || !engine?.state?.calibrationFeatures || !engine?.state?.currentSolution) return;

        set({ isCalibrating: true });

        try {
          const response = await fetch('/api/cases/calibrate-probability', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ features: engine.state.calibrationFeatures }),
          });

          if (!response.ok) throw new Error('Calibration failed');

          const { probability } = await response.json();

          set(state => ({
            engine: {
              ...state.engine,
              state: {
                ...state.engine.state,
                currentSolution: state.engine.state.currentSolution ? {
                  ...state.engine.state.currentSolution,
                  interpretation: {
                    ...state.engine.state.currentSolution.interpretation,
                    realWorldProbability: probability
                  }
                } : null,
                realProbabilityCalibrated: true
              }
            },
            isCalibrating: false
          }));          
        } catch (err) {
          console.warn("Calibration API failed", err);
          set({ isCalibrating: false });
        }
      },      

      runAutoOptimization: async () => {
        const { engine, scenario, isOptimizing, history } = get();
        if (!scenario?._id || isOptimizing) return;

        set({ isOptimizing: true, narrativeOverride: "PESA explore 4800 possibilités…" });

        try {
          const response = await fetch('/api/cases/optimize', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              caseId: scenario._id,
              currentState: engine.serialize(),
              history,
            }),
          });

          if (!response.ok) throw new Error('Optimize failed');
          const data = await response.json();

          set({
            engine: {
              ...engine,
              state: {
                ...engine.state,
                optimalPath: data,
                prediction: {
                  probability: data.probabilityOfSuccess,
                  horizon: 15,
                  timestamp: Date.now(),
                },
              },
            },
            narrativeOverride: `Chemin optimal trouvé → ${(data.probabilityOfSuccess * 100).toFixed(1)}% d’émancipation !`,
            isOptimizing: false,
          });

          setTimeout(() => set({ narrativeOverride: null }), 10000);
        } catch (err) {
          console.error(err);
          set({ narrativeOverride: "La reproduction résiste…" });
          setTimeout(() => set({ narrativeOverride: null }), 9000);
        } finally {
          set({ isOptimizing: false });
        }
      },      

      restartSimulation: () => {
        const { scenario } = get();
        if (!scenario) return;

        const freshEngine = new PesaEngine(scenario, { startTimer: false });
        freshEngine.state.isPhaseRunning = true;

        // Sécurité
        if (!freshEngine.state.completedActions) freshEngine.state.completedActions = new Set();
        if (!freshEngine.state.highlightedFamilies) freshEngine.state.highlightedFamilies = new Set();
        
        // Réinitialisation des flags bayésiens
        freshEngine.state.calibrationFeatures = {};
        freshEngine.state.realProbabilityCalibrated = false;

        // --- START TIMER si nécessaire ---
        const phase = freshEngine.state.currentPhase;
        if (phase?.type !== "action" && phase?.duration > 0) {
          freshEngine._schedulePhaseAdvance(phase.duration);
        }

        set({
          engine: freshEngine,
          phase: 'playing',
          currentCase: scenario,
          scenario: scenario,
          parameters: { ...freshEngine.state.parameters },
          initialParameters: { ...freshEngine.state.initialParameters },
          families: freshEngine.state.families,
          agents: freshEngine.state.agents,
          interactions: freshEngine.state.interactions,
          solutions: freshEngine.state.solutions,
          currentStep: freshEngine.getCurrentStep(),
          currentStepIndex: 0,
          completedActions: new Set(),
          unlockedActions: freshEngine.unlockedActions(),
          highlightedFamilies: new Set(),
          lastAction: null,
          history: [],
          narrativeOverride: null,
          currentSolution: null,
          counterfactuals: freshEngine.generateCounterfactuals(),
          prediction: null,
          currentPhase: freshEngine.state.currentPhase,
          currentPhaseIndex: 0,
          isPhaseRunning: true,
          remainingTime: freshEngine.state.remainingTime || 0,
          showDebrief: false,
          showSolution: false,
          processMap: get().getProcessMap(),
          processMapPro: get().getProcessMapPro(),
          openPanels: {
            dynamics: true,
            indicators: true,
            stepguide: true,
            history: false,
            causal: false,
            prediction: false,
            theory: false,
            insight: false,
            counterfactuals: false,
            champVivant: false,
            champVivantFullscreen: false,
            theoryFullscreen: false,
          },
          graphLayout: 'circle',
          theoryMode: false,
          isOptimizing: false,          
        });
      },
    };
  }, { name: 'PESACORE-ARCHITECT-2026' })
);

// Autorun avec subscribe
usePesa.subscribe(
  (state) => state.showDebrief,
  (showDebrief, previousShowDebrief) => {
    if (showDebrief && !previousShowDebrief) {
      const state = usePesa.getState();
      const { engine } = state;

      if (
        engine?.state?.currentSolution &&
        engine.state.calibrationFeatures &&
        !engine.state.realProbabilityCalibrated
      ) {
        // On lance la calibration
        state.calibrateRealProbability().then(() => {
          // Une fois terminée, on ouvre vraiment le débrief
          usePesa.setState({ showDebrief: true });
        });

        // On marque comme calibré (pour éviter relance)
        usePesa.setState({
          engine: {
            ...engine,
            state: {
              ...engine.state,
              realProbabilityCalibrated: true
            }
          }
        });
      } else {
        // Si déjà calibré, on ouvre directement
        usePesa.setState({ showDebrief: true });
      }
    }
  }
);


